import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import type { FieldBootstrapResponse } from '@/mobile/sync/fieldSyncTypes';
import { normalizeFieldReport } from '@/mobile/sync/normalizeFieldReport';
import type { FieldProjectListItem, FieldReportListItem } from '@/modules/field/types';
import { generateFieldId } from '@/modules/field/utils/generateId';

const CACHE_KEY = 'engetel_field_sync_cache_v2';
const CACHE_KEY_LEGACY = 'engetel_field_sync_cache_v1';
const OUTBOX_KEY = 'engetel_field_sync_outbox_v1';
const META_KEY = 'engetel_field_sync_meta_v1';
export const FIELD_SYNC_CACHE_SCHEMA = 2;

export type FieldSyncCache = {
  schemaVersion: number;
  cachedAt: string;
  lastPullAt: string | null;
  summary: FieldBootstrapResponse['summary'];
  projects: FieldProjectListItem[];
  reports: FieldReportListItem[];
  activities: FieldBootstrapResponse['activities'];
};

export type FieldSyncOutboxItem = {
  id: string;
  createdAt: string;
  operation: {
    entity: 'field_report';
    operation: 'create' | 'update';
    clientUuid: string;
    serverId?: number;
    version?: number;
    payload: Record<string, unknown>;
  };
};

type FieldSyncMeta = {
  deviceId: string;
  lastPullAt: string | null;
};

async function readJson<T>(key: string, fallback: T): Promise<T> {
  const { value } = await Preferences.get({ key });
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

async function writeJson(key: string, value: unknown): Promise<void> {
  await Preferences.set({ key, value: JSON.stringify(value) });
}

export async function getOrCreateDeviceId(): Promise<string> {
  const meta = await readJson<FieldSyncMeta | null>(META_KEY, null);
  if (meta?.deviceId) {
    return meta.deviceId;
  }

  const deviceId = generateFieldId();
  await writeJson(META_KEY, { deviceId, lastPullAt: null } satisfies FieldSyncMeta);

  return deviceId;
}

export async function getLastPullAt(): Promise<string | null> {
  const meta = await readJson<FieldSyncMeta | null>(META_KEY, null);

  return meta?.lastPullAt ?? null;
}

export async function setLastPullAt(iso: string): Promise<void> {
  const meta = await readJson<FieldSyncMeta | null>(META_KEY, null);
  const deviceId = meta?.deviceId ?? (await getOrCreateDeviceId());

  await writeJson(META_KEY, { deviceId, lastPullAt: iso } satisfies FieldSyncMeta);
}

async function purgeLegacyFieldCache(): Promise<void> {
  await Preferences.remove({ key: CACHE_KEY_LEGACY });
}

export async function loadFieldCache(): Promise<FieldSyncCache | null> {
  await purgeLegacyFieldCache();

  const raw = await readJson<FieldSyncCache | null>(CACHE_KEY, null);

  if (!raw) {
    return null;
  }

  if ((raw.schemaVersion ?? 1) < FIELD_SYNC_CACHE_SCHEMA) {
    await clearFieldCache();
    return null;
  }

  return raw;
}

function mergeListById<T extends { id: number }>(
  existing: T[],
  incoming: T[],
): T[] {
  if (incoming.length === 0) {
    return existing;
  }

  const map = new Map<number, T>();
  for (const item of existing) {
    map.set(item.id, item);
  }
  for (const item of incoming) {
    map.set(item.id, item);
  }

  return Array.from(map.values());
}

/** Grava snapshot completo (bootstrap). */
export async function saveFieldCache(data: FieldBootstrapResponse): Promise<void> {
  const payload: FieldSyncCache = {
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
    cachedAt: new Date().toISOString(),
    lastPullAt: data.serverTime,
    summary: data.summary,
    projects: data.projects,
    reports: data.reports.map((report) => normalizeFieldReport(report)),
    activities: data.activities,
  };

  await writeJson(CACHE_KEY, payload);
  await setLastPullAt(data.serverTime);
}

/** Mescla delta da sync incremental sem apagar listas quando o servidor não envia alterações. */
export async function mergeFieldCacheDelta(
  data: FieldBootstrapResponse,
): Promise<void> {
  const existing = (await loadFieldCache()) ?? {
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
    cachedAt: new Date().toISOString(),
    lastPullAt: null,
    summary: data.summary,
    projects: [],
    reports: [],
    activities: [],
  };

  const payload: FieldSyncCache = {
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
    cachedAt: new Date().toISOString(),
    lastPullAt: data.serverTime,
    summary: data.summary,
    projects: mergeListById(existing.projects, data.projects),
    reports: mergeListById(existing.reports, data.reports.map((r) => normalizeFieldReport(r))).map(
      (r) => normalizeFieldReport(r),
    ),
    activities:
      data.activities.length > 0 ? data.activities : existing.activities,
  };

  await writeJson(CACHE_KEY, payload);
  await setLastPullAt(data.serverTime);
}

export async function clearFieldCache(): Promise<void> {
  await Preferences.remove({ key: CACHE_KEY });
  const meta = await readJson<FieldSyncMeta | null>(META_KEY, null);
  if (meta?.deviceId) {
    await writeJson(META_KEY, {
      deviceId: meta.deviceId,
      lastPullAt: null,
    } satisfies FieldSyncMeta);
  }
}

export async function loadOutbox(): Promise<FieldSyncOutboxItem[]> {
  return readJson<FieldSyncOutboxItem[]>(OUTBOX_KEY, []);
}

export async function saveOutbox(items: FieldSyncOutboxItem[]): Promise<void> {
  await writeJson(OUTBOX_KEY, items);
}

export async function enqueueOutbox(
  operation: FieldSyncOutboxItem['operation'],
): Promise<void> {
  const outbox = await loadOutbox();

  outbox.push({
    id: generateFieldId(),
    createdAt: new Date().toISOString(),
    operation,
  });

  await saveOutbox(outbox);
}

export async function clearOutbox(): Promise<void> {
  await saveOutbox([]);
}
